import React, { useMemo } from 'react';

import { Box, Button, Divider, Drawer, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { CancelIcon } from '../../coreDesignSystem/icons';

import { getDrawerProps, slideProps, styles } from './AdminSlideDrawer.styles';
import { AdminSlideDrawerTestIds } from './AdminSlideDrawer.testIds';

/**
 * AdminSlideDrawer - A reusable drawer component that slides in from the right side
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Function to call when the drawer is closed
 * @param {string} props.title - The title to display in the header
 * @param {React.ReactNode} props.content - The content to display in the drawer
 * @param {React.ReactNode} props.actions - The action buttons to display in the footer
 * @param {boolean} props.isLoading - Whether the drawer is in a loading state
 * @param {string|number} props.width - Optional drawer width (e.g. '480px'). Defaults to wide drawer.
 * @param {boolean} props.hideHeader - When true, the chrome header strip (title + close
 *   button) is not rendered, letting the content supply its own header/close affordance.
 *   Defaults to false so existing consumers are unchanged.
 * @param {string} props.dataTestId - Optional test id forwarded to the root drawer element,
 *   letting each consumer scope its own drawer for automated tests.
 * @returns {React.ReactElement} The AdminSlideDrawer component
 */

const AdminSlideDrawer = ({
  open,
  onClose,
  title,
  content,
  actions = null,
  isLoading = false,
  width,
  hideHeader = false,
  dataTestId = null,
}) => {
  const memoizedContent = useMemo(() => content, [content]);
  const memoizedActions = useMemo(() => actions, [actions]);

  const drawerProps = useMemo(() => getDrawerProps(width), [width]);

  return (
    <Drawer
      hideBackdrop
      anchor="right"
      data-testid={dataTestId}
      open={open}
      onClose={onClose}
      {...drawerProps}
      SlideProps={slideProps}
    >
      <Box sx={styles.content}>
        {!hideHeader && (
          <Box sx={styles.header}>
            <Box sx={styles.title}>
              <Typography component="div" variant="bold16">
                {title}
              </Typography>
            </Box>
            <IconButton
              disableRipple
              className="close-button"
              color="inherit"
              data-testid={AdminSlideDrawerTestIds.CLOSE_BUTTON}
              sx={styles.button}
              onClick={onClose}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        )}

        <Box sx={styles.wrapper}>
          <Box sx={styles.formContent}>{memoizedContent}</Box>

          {memoizedActions && (
            <>
              <Divider sx={styles.divider} />
              <Box sx={styles.controlsWrapper}>
                <Button
                  data-testid={AdminSlideDrawerTestIds.CANCEL_BUTTON}
                  disabled={isLoading}
                  variant="outlined"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                {memoizedActions}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

AdminSlideDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  actions: PropTypes.node,
  isLoading: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideHeader: PropTypes.bool,
  dataTestId: PropTypes.string,
};

export default AdminSlideDrawer;
